import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import Loading from '../Loading'
import { DefaultFallback } from './fallback'

async function detectAdBlock(): Promise<boolean> {
	let adBlockEnabled = false
	const googleAdUrl =
		'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js'
	const controller = new AbortController()
	const timeoutId = setTimeout(() => controller.abort(), 3000)

	try {
		const keywordsToCheck = ['uBlock', 'height:1px!important']

		const response = await fetch(new Request(googleAdUrl), {
			signal: controller.signal,
		})
		clearTimeout(timeoutId)

		if (!response.headers.get('content-length')) {
			adBlockEnabled = true
		}

		const responseText = await response.text()
		if (keywordsToCheck.some((k) => responseText.includes(k))) {
			adBlockEnabled = true
		}
	} catch (error) {
		clearTimeout(timeoutId)
		// AbortError = timed out — network swallowed the request, not adblock. Fail open.
		if (!(error instanceof Error && error.name === 'AbortError')) {
			adBlockEnabled = true
		}
	} finally {
		if (typeof window !== 'undefined') {
			console.log(`AdBlock Enabled: ${adBlockEnabled}`)
		}
	}

	return adBlockEnabled
}

export function useAdBlockQuery() {
	const isBrowser = typeof window !== 'undefined'

	return useQuery({
		queryKey: ['adblock', 'enabled'],
		queryFn: detectAdBlock,
		enabled: isBrowser,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
		retry: 0,
	})
}

export type AdblockGatekeeperProps = React.PropsWithChildren<{
	fallback?: React.ReactNode
	onDetected?: () => void
	loading?: React.ReactNode
}>

export function AdblockGatekeeper({
	children,
	fallback = <DefaultFallback />,
	loading = <Loading />,
	onDetected,
}: AdblockGatekeeperProps) {
	const { data: isAdBlockEnabled, isLoading, isSuccess } = useAdBlockQuery()

	const firedRef = React.useRef(false)
	React.useEffect(() => {
		if (isSuccess && isAdBlockEnabled && !firedRef.current) {
			firedRef.current = true
			onDetected?.()
		}
	}, [isSuccess, isAdBlockEnabled, onDetected])

	if (isLoading) return <>{loading}</>

	if (!isAdBlockEnabled) return <>{children}</>

	return <>{fallback}</>
}

export default AdblockGatekeeper
